/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Clock, ArrowRight, Loader2, User, ExternalLink, Cake, CalendarDays, Phone, Mail, FileText } from "lucide-react";
import { useRouter } from "nextjs-toploader/app";
import React, { useEffect, useCallback } from "react";

import { remindersService } from "@/app/(authenticated)/reminders/_modules/services/reminders-service";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import type { Reminder, ReminderAssociation } from "@/app/_module/types/reminders";

interface ReminderFromAPI {
  id: string;
  userId: string;
  title: string;
  date: string;
  text: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  remindAt?: string;
  reminderTime?: string; // HH:mm:ss format
  associations?: ReminderAssociation[];
  associatedClientId?: string;
  associatedClientName?: string;
}

interface RemindersResponse {
  meta: {
    page: number;
    nextPage: number | null;
    prevPage: number | null;
    offset: number;
    totalItems: number;
    totalPages: number;
    itemCount: number;
  };
  items: ReminderFromAPI[];
}

export function RemindersPreview() {
  const navigate = useRouter();
  const queryClient = useQueryClient();

  // Listen for agency changes and refetch reminders
  useEffect(() => {
    const handleAgencyChange = () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
    };

    window.addEventListener('insuracentral-agency-change', handleAgencyChange);

    return () => {
      window.removeEventListener('insuracentral-agency-change', handleAgencyChange);
    };
  }, [queryClient]);

  const { data: remindersData, isLoading, error } = useQuery({
    queryKey: ["reminders", "preview"],
    queryFn: async (): Promise<RemindersResponse> => {
      try {
        // Only fetch first page for preview - limit API calls
        const response = await remindersService.getReminders({
          page: 1,
          limit: 20, // Get enough for preview
          sortBy: 'date', // Sort by reminder date, not creation date
          sortOrder: 'asc' // Get upcoming reminders first (ascending order)
        });

        if (!response || !Array.isArray(response.items)) {
          return {
            meta: {
              page: 1,
              nextPage: null,
              prevPage: null,
              offset: 0,
              totalItems: 0,
              totalPages: 1,
              itemCount: 0
            },
            items: []
          };
        }

        // Convert API response items to ReminderFromAPI format
        const pageItems: ReminderFromAPI[] = response.items.map((item: Reminder) => {
          // Find lead association to extract client info
          const leadAssociation = item.associations?.find(
            (assoc) => assoc.entityType?.toLowerCase() === 'lead'
          );
          // Find UI metadata for client name
          const uiMetadata = item.associations?.find(
            (assoc) => assoc.entityType === 'UI_METADATA'
          )?.metadata || {};

          // Extract client name from multiple sources
          const associatedClientName = (uiMetadata.associatedClientName as string) ||
            (leadAssociation?.notes?.startsWith('Associated with ')
              ? leadAssociation.notes.replace('Associated with ', '')
              : undefined) ||
            (leadAssociation?.metadata?.associatedClientName as string) ||
            undefined;

          return {
            id: item.id,
            userId: item.userId,
            title: item.title,
            date: item.date,
            text: item.text ?? "", // Ensure text is always a string
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
            deletedAt: item.deletedAt ?? null,
            remindAt: item.remindAt,
            reminderTime: typeof item.reminderTime === 'string' ? item.reminderTime : item.reminderTime instanceof Date ? `${String(item.reminderTime.getHours()).padStart(2, '0')}:${String(item.reminderTime.getMinutes()).padStart(2, '0')}:${String(item.reminderTime.getSeconds()).padStart(2, '0')}` : undefined,
            associations: item.associations,
            associatedClientId: leadAssociation?.entityId || (uiMetadata.associatedClientId as string),
            associatedClientName,
          };
        });

        return {
          meta: response.meta,
          items: pageItems
        };
      } catch (error) {

        throw error;
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - longer stale time to reduce calls
    gcTime: 15 * 60 * 1000, // 15 minutes cache time
    refetchOnWindowFocus: false, // Disable auto-refetch on focus
    retry: 2, // Reduce retry attempts
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Helper function to parse date string as local date (avoids timezone shifts)
  const parseLocalDate = (dateStr: string): Date => {
    // If it's an ISO string with time (contains T), parse normally
    if (dateStr.includes('T')) {
      return new Date(dateStr);
    }
    // For YYYY-MM-DD format, parse as local date to avoid UTC shift
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day, 12, 0, 0, 0); // Noon to avoid DST issues
  };

  const formatReminderTime = (reminder: ReminderFromAPI) => {
    // Use remindAt if available, otherwise fall back to date
    const dateStr = reminder.remindAt || reminder.date;
    const reminderDate = parseLocalDate(dateStr);

    // If we have reminderTime (HH:mm:ss), apply it to the date
    if (reminder.reminderTime && typeof reminder.reminderTime === 'string') {
      const [hours, minutes] = reminder.reminderTime.split(':').map(Number);
      reminderDate.setHours(hours, minutes, 0, 0);
    }

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    // Reset time to compare just dates
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const tomorrowStart = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());
    const reminderStart = new Date(reminderDate.getFullYear(), reminderDate.getMonth(), reminderDate.getDate());

    if (reminderStart.getTime() === todayStart.getTime()) {
      return reminderDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } else if (reminderStart.getTime() === tomorrowStart.getTime()) {
      return `Tomorrow ${reminderDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })}`;
    } else {
      return reminderDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
  };

  const isUpcomingReminder = (reminder: ReminderFromAPI) => {
    // Use remindAt if available, otherwise fall back to date
    const dateStr = reminder.remindAt || reminder.date;
    const reminderDate = parseLocalDate(dateStr);

    // If we have reminderTime (HH:mm:ss), apply it to the date
    if (reminder.reminderTime && typeof reminder.reminderTime === 'string') {
      const [hours, minutes] = reminder.reminderTime.split(':').map(Number);
      reminderDate.setHours(hours, minutes, 0, 0);
    }

    const now = new Date();
    const reminderTime = reminderDate.getTime();
    const currentTime = now.getTime();

    // Show reminders that are in the future (haven't passed yet)
    return reminderTime >= currentTime;
  };

  const getUpcomingReminders = (reminders: ReminderFromAPI[]) => {
    const getDateTime = (reminder: ReminderFromAPI) => {
      const dateStr = reminder.remindAt || reminder.date;
      const date = parseLocalDate(dateStr);
      if (reminder.reminderTime && typeof reminder.reminderTime === 'string') {
        const [hours, minutes] = reminder.reminderTime.split(':').map(Number);
        date.setHours(hours, minutes, 0, 0);
      }
      return date.getTime();
    };

    const upcoming = reminders
      .filter(reminder => {
        return !reminder.deletedAt && isUpcomingReminder(reminder);
      })
      .sort((a, b) => getDateTime(a) - getDateTime(b))
      .slice(0, 4);

    return upcoming;
  };

  const upcomingReminders = remindersData?.items ? getUpcomingReminders(remindersData.items) : [];

  const getTypeFromTitle = (title: string) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('birthday')) return 'birthday';
    if (lowerTitle.includes('meeting') || lowerTitle.includes('appointment')) return 'meeting';
    if (lowerTitle.includes('call') || lowerTitle.includes('phone')) return 'call';
    if (lowerTitle.includes('email') || lowerTitle.includes('follow up')) return 'email';
    return 'reminder';
  };

  const getTypeIcon = (title: string) => {
    const type = getTypeFromTitle(title);
    switch (type) {
      case "birthday": return <Cake className="h-5 w-5 text-primary" />;
      case "meeting": return <CalendarDays className="h-5 w-5 text-primary" />;
      case "call": return <Phone className="h-5 w-5 text-primary" />;
      case "email": return <Mail className="h-5 w-5 text-primary" />;
      default: return <FileText className="h-5 w-5 text-primary" />;
    }
  };

  // Inside the native shell every screen lives under /m.
  const shellPrefix = typeof window !== "undefined" && window.location.pathname.startsWith("/m") ? "/m" : "";
  const goToReminders = () => navigate.push(`${shellPrefix}/reminders`);

  // Get lead ID from reminder associations
  const getLeadId = useCallback((reminder: ReminderFromAPI): string | null => {
    // First check associations array for lead type
    const leadAssociation = reminder.associations?.find(
      (assoc) => assoc.entityType?.toLowerCase() === 'lead'
    );
    if (leadAssociation?.entityId) return leadAssociation.entityId;
    // Fall back to associatedClientId
    if (reminder.associatedClientId) return reminder.associatedClientId;
    return null;
  }, []);

  // Handle reminder click - navigate to lead if associated, otherwise to reminders
  const handleReminderClick = useCallback((reminder: ReminderFromAPI) => {
    const leadId = getLeadId(reminder);
    if (leadId) {
      navigate.push(`${shellPrefix}/leads/${leadId}`);
    } else {
      goToReminders();
    }
  }, [getLeadId, navigate]);

  if (isLoading) {
    return (
      <Card className="h-full shadow-sm border-gray-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-lg font-semibold text-gray-900">
            Upcoming Reminders
          </CardTitle>
          <Bell className="h-5 w-5 text-primary" />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center py-8 text-gray-500">
            <Loader2 className="h-8 w-8 mx-auto mb-3 text-gray-400 animate-spin" />
            <p className="text-sm font-medium">Loading reminders...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-full shadow-sm border-gray-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-lg font-semibold text-gray-900">
            Upcoming Reminders
          </CardTitle>
          <Bell className="h-5 w-5 text-primary" />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center py-8 text-gray-500">
            <Bell className="h-12 w-12 mx-auto mb-3 text-red-300" />
            <p className="text-sm font-medium text-red-600">Unable to load reminders</p>
            <p className="text-xs text-gray-400 mt-1">
              {error instanceof Error ? error.message : 'Please try again later'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full shadow-sm border-gray-200">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-lg font-semibold text-gray-900">
          Upcoming Reminders
        </CardTitle>
        <Bell className="h-5 w-5 text-primary" />
      </CardHeader>
      <CardContent className="space-y-4">
        {upcomingReminders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm font-medium">No upcoming reminders</p>
            <p className="text-xs text-gray-400 mt-1">
              {remindersData?.items?.length ? 
                `${remindersData.items.length} total reminders found` : 
                'Your schedule is clear'
              }
            </p>
          </div>
        ) : (
          <React.Fragment>
            <div className="space-y-3">
              {upcomingReminders.map((reminder) => {
                const leadId = getLeadId(reminder);
                return (
                  <div
                    key={reminder.id}
                    className="flex items-start gap-4 p-4 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-all duration-200 cursor-pointer"
                    onClick={() => handleReminderClick(reminder)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleReminderClick(reminder);
                      }
                    }}
                  >
                    <div className="mt-0.5">
                      {getTypeIcon(reminder.title)}
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <p className="text-sm font-semibold text-gray-900 leading-tight">
                        {reminder.title}
                      </p>
                      {!!reminder.text && (
                        <p className="text-xs text-gray-600 leading-tight">
                          {reminder.text}
                        </p>
                      )}
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5 text-gray-400" />
                          <span className="text-xs text-gray-600 font-medium">
                            {formatReminderTime(reminder)}
                          </span>
                        </div>
                        <Badge className="bg-primary/20 text-primary border-primary/20 text-xs border font-medium">
                          {getTypeFromTitle(reminder.title)}
                        </Badge>
                      </div>
                      {!!reminder.associatedClientName && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <User className="h-3.5 w-3.5 text-gray-400" />
                          <span className={leadId ? "text-primary font-medium" : ""}>
                            {reminder.associatedClientName}
                          </span>
                          {!!leadId && <ExternalLink className="h-3 w-3 text-primary" />}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="pt-3 border-t border-gray-200">
              <Button
                className="w-full justify-between text-primary hover:text-primary/80 hover:bg-primary/10 h-10 font-medium"
                onClick={() => navigate.push(`${shellPrefix}/reminders`)}
                variant="ghost"
              >
                View all reminders
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </React.Fragment>
        )}
      </CardContent>
    </Card>
  );
}